import { useQuery } from "@apollo/client";
import { Box, Card, CardActionArea, Collapse, Skeleton, Stack, Typography } from "@mui/material";
import { useOmnisearchState } from "hooks/omnisearch-v2";
import { ReactNode, useCallback } from "react";

import { UNIFIED_WORK_STATS_QUERY } from "./api";

const numberFormatter = Intl.NumberFormat("en", { notation: "standard" });

function StatCard({
  label,
  value,
  subText,
  loading,
  active,
  onClick,
}: {
  label: string;
  value?: number;
  subText?: string;
  loading: boolean;
  active?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <Box sx={{ px: 2, py: 1.25 }}>
      {loading ? (
        <Skeleton width={56} sx={{ fontSize: "1.5rem" }} />
      ) : (
        <Typography variant="h5" component="div">
          {numberFormatter.format(value ?? 0)}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" noWrap component="div">
        {label}
      </Typography>
      {subText !== undefined && (
        <Typography variant="caption" color="text.disabled" noWrap component="div">
          {loading ? <Skeleton width={64} /> : subText}
        </Typography>
      )}
    </Box>
  );

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        minWidth: 132,
        borderColor: active ? "primary.main" : undefined,
        backgroundColor: active ? theme.palette.action.selected : undefined,
      })}
    >
      {onClick ? <CardActionArea onClick={onClick}>{content}</CardActionArea> : content}
    </Card>
  );
}

function StatGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {title}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {children}
      </Stack>
    </Stack>
  );
}

/**
 * Collapsible stat cards above the work queue: assignment counts and the
 * per-work-type breakdown, aggregated by unifiedWorkStats over the same
 * omnisearch string the grid sends — so the cards always describe the rows
 * below them. Clicking a card toggles the matching grid filter.
 */
export function WorkStats({ open }: { open: boolean }) {
  const { omnisearch, getOmnisearchValue, editOmnisearchKeyValues, removeOmnisearchKeyValue } =
    useOmnisearchState();

  const { data, previousData, loading } = useQuery(UNIFIED_WORK_STATS_QUERY, {
    variables: { search: omnisearch },
    skip: !open,
  });

  // Keep the previous numbers on screen while a filter change refetches;
  // skeletons only on first open.
  const stats = (data ?? previousData)?.unifiedWorkStats;
  const showSkeletons = loading && !stats;

  const toggleFilter = useCallback(
    (key: string, value: string) => {
      if (getOmnisearchValue(key) === value) {
        removeOmnisearchKeyValue(key);
      } else {
        editOmnisearchKeyValues([[key, value]]);
      }
    },
    [getOmnisearchValue, editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  const assigneeFilter = getOmnisearchValue("assigneeEmail");
  const workTypeFilter = getOmnisearchValue("workType");

  return (
    <Collapse in={open}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={{ xs: 1.5, lg: 4 }} sx={{ pb: 1 }}>
        <StatGroup title="Assignment">
          <StatCard label="Open items" value={stats?.total} loading={showSkeletons} />
          <StatCard
            label="Assigned"
            value={stats?.assigned}
            loading={showSkeletons}
            active={assigneeFilter === "!null"}
            onClick={() => toggleFilter("assigneeEmail", "!null")}
          />
          <StatCard
            label="Unassigned"
            value={stats?.unassigned}
            loading={showSkeletons}
            active={assigneeFilter === "null"}
            onClick={() => toggleFilter("assigneeEmail", "null")}
          />
        </StatGroup>
        <StatGroup title="Work types">
          {showSkeletons &&
            Array.from({ length: 3 }).map((_, i) => (
              <StatCard key={i} label="" subText="" loading />
            ))}
          {!showSkeletons && stats?.byWorkType.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
              No open work matches this view.
            </Typography>
          )}
          {stats?.byWorkType.map((workType) => (
            <StatCard
              key={workType.workTypeKey ?? workType.workTypeLabel ?? "unknown"}
              label={workType.workTypeLabel ?? "Unknown type"}
              value={workType.count}
              subText={`${numberFormatter.format(workType.assigned)} assigned`}
              loading={false}
              active={workTypeFilter === workType.workTypeKey}
              onClick={
                workType.workTypeKey
                  ? () => toggleFilter("workType", workType.workTypeKey ?? "")
                  : undefined
              }
            />
          ))}
        </StatGroup>
      </Stack>
    </Collapse>
  );
}
